/**
 * Make React resilient to DOM mutations caused by third-party tools such as
 * browser auto-translation (Google Translate / Chrome, Edge, Samsung Internet).
 *
 * These tools replace text nodes with <font> wrappers. When React later tries
 * to remove/reorder those nodes during reconciliation, the native
 * removeChild/insertBefore throw:
 *   "Failed to execute 'removeChild' on 'Node': The node to be removed is not
 *    a child of this node."
 * which crashes the whole app (white screen).
 *
 * This guard (the well-known React #11538 workaround) makes those operations
 * no-op safely instead of throwing, while keeping translation usable.
 */
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      if (typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn('Ignored removeChild on a node with a different parent (likely browser translation).');
      }
      return child;
    }
    // eslint-disable-next-line prefer-rest-params
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn('Ignored insertBefore with a reference node from a different parent (likely browser translation).');
      }
      return newNode;
    }
    // eslint-disable-next-line prefer-rest-params
    return originalInsertBefore.apply(this, arguments);
  };
}
